import { useState, useMemo, DragEvent } from 'react';
import { Plus, Search, BarChart3, Box } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/shared/components/ui/sheet';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Card } from '@/shared/components/ui/card';
import { ScrollArea } from '@/shared/components/ui/scroll-area';
import { useTranslation } from 'react-i18next';
import { useAvailableDashboards } from '../hooks/useAvailableDashboards';
import { useDashboards } from '../hooks/useDashboards';
import { useDashboardTabs } from '../hooks/useDashboardTabs';
import { useViewScope } from '@/shared/hooks/use-view-scope';
import { Dashboard } from '../types/Dashboard';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useQuery } from '@tanstack/react-query';
import { IntegrationBaseService } from '@/features/Integrations/services/integrationsBaseService';
import { AgentBaseService } from '@/features/Agents/services/agentBaseService';
import { getLocalizedText } from '@/shared/utils/i18nHelper';
import { FormFactor, getFormFactor, isCompatible } from '../utils/formFactor';
import { useDashboardDrag, DRAG_MIME_TYPE } from '../state/DashboardDragContext';

export interface SlotContext {
  slotId: string;
  formFactor: FormFactor;
}

interface AddDashboardFlyoutProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId?: string;
  slotContext?: SlotContext | null;
  onAssignToSlot?: (slotId: string, dashboardBaseId: string) => void;
  // Caminho "add sem slot": chamado quando o usuário clica em um dashboard
  // do flyout SEM ter aberto via um slot específico. O caller é responsável
  // por marcar visible e — em layout custom — criar slot compatível caso
  // não exista. Quando não passado, fallback é apenas addDashboard direto.
  onAddNoSlot?: (dashboard: Dashboard) => void;
  // Dashboards instalados que NÃO cabem no layout ativo. Não vêm do
  // endpoint "available" (lá são filtrados por preferences), mas precisam
  // aparecer aqui para que o usuário possa re-atribuí-los manualmente.
  extraDashboards?: Dashboard[];
}

export const AddDashboardFlyout = ({
  isOpen,
  onClose,
  workspaceId,
  slotContext,
  onAssignToSlot,
  onAddNoSlot,
  extraDashboards,
}: AddDashboardFlyoutProps) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language?.split('-')[0] || 'pt';
  const { scope: viewScope, organizationId: viewOrgId } = useViewScope();
  const { currentTabId } = useDashboardTabs();
  // PBI #78540: organizationId do useViewScope já resolve a org efetiva
  // (selectedOrganization do superadmin > org da sessão). Sem repassar, o
  // flyout do superadmin em workspace de outra org vem vazio.
  const { availableDashboards: rawAvailable, isLoading } = useAvailableDashboards(
    workspaceId,
    viewScope,
    currentTabId,
    viewOrgId ?? undefined,
  );
  const availableDashboards = useMemo(() => {
    if (!extraDashboards || extraDashboards.length === 0) return rawAvailable;
    const byId = new Map<string, Dashboard>();
    for (const d of rawAvailable) byId.set(d.id, d);
    for (const d of extraDashboards) if (!byId.has(d.id)) byId.set(d.id, d);
    return Array.from(byId.values());
  }, [rawAvailable, extraDashboards]);
  const { addDashboard, isAddingDashboard } = useDashboards(workspaceId);
  const { startExternalDrag, endExternalDrag } = useDashboardDrag();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Buscar todas as integration bases para os dashboards
  const integrationIds = useMemo(() => {
    const ids = new Set(
      availableDashboards
        .filter((d) => d.source_type === 'integration' && d.source_id)
        .map((d) => d.source_id)
    );
    return Array.from(ids);
  }, [availableDashboards]);

  const { data: integrationBases = [] } = useQuery({
    queryKey: ['integration-bases-bulk', integrationIds],
    queryFn: async () => {
      const results = await Promise.all(
        integrationIds.map((id) =>
          IntegrationBaseService.getById(id).catch(() => null)
        )
      );
      return results.filter(Boolean);
    },
    enabled: integrationIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // PBI #78540 — mesma idéia, pra agent-owned dashboards. source_id é o
  // agent_base.id; usamos a logo herdada do template (gravada em
  // agent_base.logo_url no install) pra renderizar no header do card do
  // flyout, mesmo padrão das integrations.
  const agentIds = useMemo(() => {
    const ids = new Set(
      availableDashboards
        .filter((d) => d.source_type === 'agent' && d.source_id)
        .map((d) => d.source_id)
    );
    return Array.from(ids);
  }, [availableDashboards]);

  const { data: agentBases = [] } = useQuery({
    queryKey: ['agent-bases-bulk', agentIds],
    queryFn: async () => {
      const results = await Promise.all(
        agentIds.map((id) => AgentBaseService.getById(id).catch(() => null)),
      );
      return results.filter(Boolean);
    },
    enabled: agentIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const categories = useMemo(() => {
    const cats = new Set(availableDashboards.map((d) => d.category));
    return Array.from(cats);
  }, [availableDashboards]);

  // Helper para parsear campos que podem vir como JSON string
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const parseIfJSON = (value: any) => {
    if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    }
    return value;
  };

  const filteredDashboards = useMemo(() => {
    return availableDashboards.filter((dashboard) => {
      const translatedName = getLocalizedText(parseIfJSON(dashboard.name), currentLanguage);
      const translatedDesc = getLocalizedText(parseIfJSON(dashboard.description), currentLanguage);
      const matchesSearch =
        translatedName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        translatedDesc.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = !selectedCategory || dashboard.category === selectedCategory;
      const matchesSlot =
        !slotContext || isCompatible(slotContext.formFactor, dashboard.type);
      return matchesSearch && matchesCategory && matchesSlot;
    });
  }, [availableDashboards, searchTerm, selectedCategory, currentLanguage, slotContext]);

  const handleAddDashboard = (dashboard: Dashboard) => {
    if (slotContext && onAssignToSlot) {
      onAssignToSlot(slotContext.slotId, dashboard.id);
      onClose();
      return;
    }
    if (onAddNoSlot) {
      onAddNoSlot(dashboard);
      onClose();
      return;
    }
    addDashboard(
      { dashboardBaseId: dashboard.id },
      {
        onSuccess: () => {
          onClose();
        },
      }
    );
  };

  const handleDragStart = (e: DragEvent<HTMLDivElement>, dashboard: Dashboard) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData(DRAG_MIME_TYPE, dashboard.id);
    e.dataTransfer.setData('text/plain', dashboard.id);
    startExternalDrag({
      externalDashboardBaseId: dashboard.id,
      formFactor: getFormFactor(dashboard.type),
    });
  };

  const handleDragEnd = () => endExternalDrag();

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      metrics: 'bg-blue-500/10 text-blue-500 hover:bg-blue-500/20',
      analytics: 'bg-purple-500/10 text-purple-500 hover:bg-purple-500/20',
      reports: 'bg-green-500/10 text-green-500 hover:bg-green-500/20',
      chart: 'bg-primary/10 text-primary hover:bg-primary/20',
      tiles: 'bg-primary/10 text-primary hover:bg-primary/20',
    };
    return colors[category.toLowerCase()] || 'bg-muted text-muted-foreground hover:bg-muted/80';
  };

  const getCategoryLabel = (category: string) => {
    // Tenta traduzir, se não existir retorna o valor original capitalizado
    const translationKey = `dashboards.categories.${category}`;
    const translated = t(translationKey);
    
    // Se a tradução retornar a própria chave, significa que não existe
    if (translated === translationKey) {
      // Retorna o valor original capitalizado
      return category.charAt(0).toUpperCase() + category.slice(1);
    }
    
    return translated;
  };

  const getChartTypeLabel = (type: string) => {
    // Tenta traduzir
    const translationKey = `dashboards.chartTypes.${type}`;
    const translated = t(translationKey);
    
    // Se retornar a própria chave = tradução não existe
    if (translated === translationKey) {
      // Formata o tipo: "gauge-multiple" -> "Gauge Multiple"
      return type
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    
    return translated;
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose} modal={false}>
      <SheetContent className="w-full sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{t('dashboards.addDashboard')}</SheetTitle>
          <SheetDescription>{t('dashboards.addDashboardDescription')}</SheetDescription>
        </SheetHeader>

        <div className="space-y-4 mt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/60 z-10 pointer-events-none" />
            <Input
              placeholder={t('dashboards.searchDashboards')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <Badge
              variant={selectedCategory === null ? 'default' : 'outline'}
              className="cursor-pointer"
              onClick={() => setSelectedCategory(null)}
            >
              {t('dashboards.allCategories')}
            </Badge>
            {categories.map((category) => (
              <Badge
                key={category}
                variant={selectedCategory === category ? 'default' : 'outline'}
                className={`cursor-pointer ${selectedCategory === category ? '' : getCategoryColor(category)}`}
                onClick={() => setSelectedCategory(category)}
              >
                {getCategoryLabel(category)}
              </Badge>
            ))}
          </div>

          <ScrollArea className="h-[calc(100vh-300px)]">
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Card key={i} className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-5 w-32" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-20" />
                      </div>
                      <Skeleton className="h-9 w-24" />
                    </div>
                  </Card>
                ))}
              </div>
            ) : filteredDashboards.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <BarChart3 className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-sm text-muted-foreground">{t('dashboards.noDashboardsAvailable')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredDashboards.map((dashboard) => {
                  const integrationBase = dashboard.source_type === 'integration'
                    ? integrationBases.find((ib) => ib?.id === dashboard.source_id)
                    : null;
                  const agentBase = dashboard.source_type === 'agent'
                    ? agentBases.find((ab) => ab?.id === dashboard.source_id)
                    : null;
                  // PBI #78540 — logo do agent reusa o slot do logo da
                  // integration (mesmo posicionamento + estilo no card).
                  const logoUrl = integrationBase?.logo_url ?? agentBase?.logo_url;
                  const integrationName = integrationBase?.label ?? agentBase?.name;
                  const translatedName = getLocalizedText(parseIfJSON(dashboard.name), currentLanguage);
                  const translatedDesc = getLocalizedText(parseIfJSON(dashboard.description), currentLanguage);
                  
                  return (
                    <Card
                      key={dashboard.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, dashboard)}
                      onDragEnd={handleDragEnd}
                      className="p-4 hover:bg-muted/50 transition-colors cursor-grab active:cursor-grabbing"
                    >
                      <div className="flex items-start gap-4">
                        {/* Logo da integração */}
                        <div className="flex-shrink-0 mt-1">
                          {logoUrl ? (
                            <img 
                              src={logoUrl} 
                              alt="Integration logo"
                              className="h-8 w-8 object-contain rounded"
                            />
                          ) : (
                            <div className="h-8 w-8 rounded bg-muted flex items-center justify-center">
                              <Box className="h-4 w-4 text-muted-foreground" />
                            </div>
                          )}
                        </div>

                        {/* Conteúdo */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex-1 min-w-0">
                              {integrationName && (
                                <p className="text-xs text-muted-foreground mb-0.5">{integrationName}</p>
                              )}
                              <h4 className="font-semibold text-sm truncate mb-1">{translatedName}</h4>
                              <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{translatedDesc}</p>
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="outline" className={`text-xs ${getCategoryColor(dashboard.category)}`}>
                                  {getCategoryLabel(dashboard.category)}
                                </Badge>
                                <Badge variant="secondary" className="text-xs">
                                  {getChartTypeLabel(dashboard.type)}
                                </Badge>
                              </div>
                            </div>
                            <Button
                              size="icon"
                              variant="outline"
                              onClick={() => handleAddDashboard(dashboard)}
                              disabled={isAddingDashboard}
                              className="flex-shrink-0 h-9 w-9 rounded-full"
                              title={t('dashboards.add')}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </div>
      </SheetContent>
    </Sheet>
  );
};
